import React from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Typography
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import TagOutlinedIcon from '@mui/icons-material/TagOutlined';
import LaunchIcon from '@mui/icons-material/Launch';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import { formatDateTime } from '../../utils/dateFormat';
import { useGroupCenters } from './useGroupCenters';

const getText = (value) => (typeof value === 'string' ? value.trim() : value);
const formatOrgType = (value) => {
  if (!value) return '';
  return String(value).replace(/_/g, ' ');
};

const DetailRow = ({ icon, label, value }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: '20px 126px minmax(0, 1fr)',
      columnGap: 1,
      alignItems: 'center',
      minHeight: 28
    }}
  >
    <Box
      sx={{
        width: 20,
        height: 20,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        color: 'text.secondary',
        lineHeight: 0,
        '& svg': { fontSize: 16 }
      }}
    >
      {icon}
    </Box>
    <Typography
      variant="caption"
      sx={{
        color: 'text.secondary',
        fontWeight: 600,
        lineHeight: 1.35,
        display: 'flex',
        alignItems: 'center'
      }}
    >
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{
        minWidth: 0,
        color: 'text.primary',
        fontWeight: 500,
        overflowWrap: 'anywhere',
        lineHeight: 1.35,
        textAlign: 'right'
      }}
    >
      {value}
    </Typography>
  </Box>
);

const WorkMetric = ({ value, label, color }) => (
  <Box sx={{ minWidth: 0, textAlign: 'center' }}>
    <Typography sx={{ color, fontWeight: 800, fontSize: '1.1rem', lineHeight: 1.1 }}>
      {value}
    </Typography>
    <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.2, display: 'block' }}>
      {label}
    </Typography>
  </Box>
);

const GroupCenters = () => {
  const { centers, centerMetrics, loading, error, opening, openCenter } = useGroupCenters();

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 600, color: 'secondary.main', mb: 0.5 }}>
            Typing Centers
          </Typography>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : centers.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
          No typing centers are assigned to your group yet.
        </Paper>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(auto-fill, minmax(320px, 390px))'
            },
            gap: 2.5,
            justifyContent: 'start',
            alignItems: 'stretch'
          }}
        >
          {centers.map((tc) => (
            <Box key={tc.id} sx={{ display: 'flex', minWidth: 0 }}>
              <Card
                variant="outlined"
                sx={{
                  height: '100%',
                  width: '100%',
                  maxWidth: { xs: 'none', sm: 390 },
                  minHeight: 360,
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'box-shadow 0.2s ease, transform 0.2s ease',
                  '&:hover': {
                    boxShadow: 3,
                    transform: 'translateY(-2px)'
                  }
                }}
              >
                <CardContent sx={{ flexGrow: 1, p: 2.25, display: 'flex', flexDirection: 'column', '&:last-child': { pb: 2.25 } }}>
                  {(() => {
                    const primaryDetails = [
                      { icon: <EmailOutlinedIcon />, label: 'Email', value: getText(tc.email) },
                      { icon: <PhoneOutlinedIcon />, label: 'Contact Number', value: getText(tc.contactNumber) },
                      { icon: <PersonOutlineIcon />, label: 'Contact Person', value: getText(tc.contactPerson) },
                      { icon: <LocationOnOutlinedIcon />, label: 'Address', value: getText(tc.address) }
                    ].filter((item) => item.value);
                    const secondaryDetails = [
                      { icon: <BusinessIcon />, label: 'Organization Type', value: formatOrgType(tc.organizationType) },
                      { icon: <CalendarTodayOutlinedIcon />, label: 'Created At', value: formatDateTime(tc.createdAt, '') },
                      { icon: <PersonOutlineIcon />, label: 'Created By', value: getText(tc.createdBy) },
                      { icon: <TagOutlinedIcon />, label: 'Code', value: getText(tc.code) }
                    ].filter((item) => item.value);
                    const detailItems = [...primaryDetails, ...secondaryDetails];
                    const metrics = centerMetrics[tc.id] || {};

                    return (
                      <>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                          <Avatar
                            src={tc.logoUrl || undefined}
                            variant="rounded"
                            sx={{ width: 42, height: 42, bgcolor: 'primary.light', flexShrink: 0 }}
                          >
                            <BusinessIcon />
                          </Avatar>
                          <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.25 }} noWrap>
                              {tc.name}
                            </Typography>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.75 }}>
                              <Chip
                                label={tc.active ? 'Active' : 'Inactive'}
                                color={tc.active ? 'success' : 'default'}
                                size="small"
                                sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600 }}
                              />
                            </Stack>
                          </Box>
                          <Button
                            size="small"
                            variant="outlined"
                            endIcon={<LaunchIcon />}
                            disabled={opening === tc.id}
                            onClick={() => openCenter(tc)}
                            sx={{ flexShrink: 0, textTransform: 'none', minWidth: 78 }}
                          >
                            {opening === tc.id ? 'Opening' : 'Open'}
                          </Button>
                        </Box>

                        {(primaryDetails.length > 0 || secondaryDetails.length > 0) && <Divider sx={{ my: 2 }} />}

                        <Box sx={{ flexGrow: 1 }}>
                          {detailItems.length > 0 ? (
                            <Box sx={{ display: 'grid', rowGap: 0.5 }}>
                              {detailItems.map((item) => (
                                <DetailRow key={item.label} {...item} />
                              ))}
                            </Box>
                          ) : (
                            <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
                              Contact details have not been added yet.
                            </Typography>
                          )}
                        </Box>

                        <Box sx={{ mt: 'auto' }}>
                          <Divider sx={{ my: 2 }} />
                          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 1 }}>
                            <WorkMetric value={metrics.totalWork || 0} label="Work Orders" color="primary.main" />
                            <WorkMetric value={metrics.invoicedWork || 0} label="Invoiced" color="secondary.main" />
                            <WorkMetric value={metrics.inProgressWork || 0} label="In Progress" color="warning.main" />
                            <WorkMetric value={metrics.completedWork || 0} label="Completed" color="success.main" />
                          </Box>
                        </Box>
                      </>
                    );
                  })()}
                </CardContent>
              </Card>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default GroupCenters;
